import * as React from 'react';
import classNames from 'classnames';
import { Text } from '@wix/design-system';
import { Vertical, MediaDevices } from 'enums';
import { RootStore, withInjectedRootStore } from 'providers';
import { AdvancedPlansFeatureSection, AdvancedPlansProductData } from 'types';
import { FeatureRow } from '../AdvancedPlans/PlansGrid/FeatureRow';
import { ShowFeaturesButton } from './ShowFeaturesButton';
import s from './PlanSections.scss';
import { usePageModeContext } from 'providers/PageMode/PageMode.provider';
import { useTranslation } from 'react-i18next';
import { Collapse } from '@wix/package-picker-components';
import { getRecommendedSectionDataFromDealerOffers } from 'utils';
import { useBreakpoint } from 'providers/BreakpointProvider';

interface PlansSectionsProps {
  section: AdvancedPlansFeatureSection;
  products: AdvancedPlansProductData[];
  rootStore: RootStore;
  sectionsAreFullyVisible: boolean;
}

const PlanSectionsComp: React.FC<PlansSectionsProps> = ({
  section,
  products,
  rootStore,
  sectionsAreFullyVisible,
}) => {
  const {
    appContext: { biLogger, siteGuid, verticalName, parsedDealerOffers },
    planType,
    isSecondUpgrade,
    isUseDealerIntegrationIntentElementsEnabled,
    isJapanesePackagePicker,
  } = rootStore.appStore;
  const { isInSale } = rootStore.appStore;
  let shouldShowBadge, isSectionPreOpened;

  const { currentBreakpoint } = useBreakpoint();

  const tooltipsAsASubtitle =
    isJapanesePackagePicker && currentBreakpoint === MediaDevices.Desktop;

  const isSectionPreOpenedByServerIntent = () => {
    if (verticalName === Vertical.Default) {
      return !!section.alwaysOpened;
    }
    return section.intent.includes(verticalName as Vertical);
  };

  if (isUseDealerIntegrationIntentElementsEnabled) {
    const { recommendedSection, shouldShowBadgeByDealer } =
      getRecommendedSectionDataFromDealerOffers(
        parsedDealerOffers,
        isSecondUpgrade,
      );
    isSectionPreOpened = section.category === recommendedSection;
    shouldShowBadge = shouldShowBadgeByDealer && isSectionPreOpened;
  } else {
    isSectionPreOpened = isSectionPreOpenedByServerIntent();
    shouldShowBadge =
      verticalName !== Vertical.Default &&
      verticalName !== Vertical.PaidPlans &&
      section.intent.includes(verticalName as Vertical);
  }

  const [isFeaturesVisible, setFeaturesVisible] =
    React.useState(isSectionPreOpened);

  const toggleFeaturesVisibility = React.useCallback(
    (sectionTitle) => {
      biLogger.logClickOnShowFeatures({
        showFeatureType: sectionTitle,
        clickType: isFeaturesVisible ? 'hide' : 'show',
        ppType: planType,
        siteGuid,
      });
      setFeaturesVisible((prev) => !prev);
    },
    [isFeaturesVisible],
  );
  const { t } = useTranslation();
  const { theme: themeName } = usePageModeContext();

  return (
    <div className={s.sectionsWrapper}>
      <div
        className={classNames(s.section, themeName, {
          [s.sticky]: sectionsAreFullyVisible,
        })}
        data-testid='section'
        onClick={() => toggleFeaturesVisibility(section.translationKey)}
      >
        <div className={s.sectionData}>
          <div className={s.sectionNameContainer}>
            <Text dataHook='section-name' className={s.sectionName}>
              {t(section.translationKey)}
            </Text>
            {section.icon && (
              <>
                <div className={s.sectionIconDelimiter} />
                <img className={s.sectionIcon} src={section.icon} />
              </>
            )}
            {shouldShowBadge && (
              <Text
                size='tiny'
                dataHook='badge'
                className={classNames(s.badge, {
                  'sale-orange-color': isInSale,
                })}
                skin='premium'
                weight='bold'
              >
                {t('Advanced.Plans_General_Tailored_for_your_business')}
              </Text>
            )}
          </div>
          <div data-testid='section-description'>
            <Text size='small' className={s.sectionDescription}>
              {t(section.descriptionTranslationKey)}
            </Text>
          </div>
        </div>
        <ShowFeaturesButton isFeaturesVisible={isFeaturesVisible} />
      </div>
      <Collapse
        className={classNames(s.sectionsSubgrid, {
          [s.japanSectionsSubgrid]: tooltipsAsASubtitle,
        })}
        isOpened={isFeaturesVisible}
      >
        <div data-testid='section-features' className={s.sectionFeatures}>
          {section.features.map((feature, idx) => (
            <FeatureRow key={idx} products={products} feature={feature} />
          ))}
        </div>
      </Collapse>
    </div>
  );
};

export const PlanSections = withInjectedRootStore(PlanSectionsComp);
